import type { TaskListItem } from '@/hooks/useTasks';

/**
 * Client mirror of the staff-api task guards (services/staff-api routes/tasks.ts),
 * used only to decide which calendar affordances to offer. The server stays the
 * authority: a refused write still comes back as 403 and is reported.
 */
export interface TaskActor {
  userId: string | undefined;
  role: string | undefined;
  hasPermission: (permission: string) => boolean;
}

const TASK_MANAGER_ROLES = ['manager', 'admin', 'developer'];

function isOpen(task: Pick<TaskListItem, 'status'>): boolean {
  return task.status !== 'completed' && task.status !== 'cancelled';
}

/** PATCH (reschedule): tasks.manage, or the assignee / creator of the task. */
export function canRescheduleTask(
  actor: TaskActor,
  task: Pick<TaskListItem, 'status' | 'assignedTo' | 'createdBy'>,
): boolean {
  if (!isOpen(task) || !actor.userId) return false;
  return (
    actor.hasPermission('tasks.manage') ||
    task.assignedTo?.id === actor.userId ||
    task.createdBy.id === actor.userId
  );
}

/** POST /:id/complete: a task manager role, an unassigned task, or its assignee. */
export function canCompleteTask(
  actor: TaskActor,
  task: Pick<TaskListItem, 'status' | 'assignedTo'>,
): boolean {
  if (!isOpen(task) || !actor.userId) return false;
  return (
    (actor.role !== undefined && TASK_MANAGER_ROLES.includes(actor.role)) ||
    !task.assignedTo ||
    task.assignedTo.id === actor.userId
  );
}
